import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  CheckSquare, Square, Plus, Trash, PencilSimple, X, FloppyDisk,
  Calendar, User, Flag, Sparkle, WhatsappLogo, CaretDown, CaretUp,
  Phone,
} from '@phosphor-icons/react';

const PRIORITIES = [
  { id: 'low',    label: 'Low',    color: '#64748B' },
  { id: 'normal', label: 'Normal', color: '#3B82F6' },
  { id: 'high',   label: 'High',   color: '#EF4444' },
];

export default function TasksTab({ event }) {
  const { api } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({ open: 0, done: 0 });
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('all'); // all | open | done | overdue

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, tplR] = await Promise.all([
        api.get(`/events/${event.event_id}/tasks`),
        api.get('/events/tasks/templates'),
      ]);
      setTasks(r.data.tasks || []);
      setCounts({ open: r.data.open, done: r.data.done });
      setTemplates(tplR.data.templates || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, event.event_id]);

  useEffect(() => { load(); }, [load]);

  const toggleDone = async (task) => {
    const next = task.status === 'done' ? 'open' : 'done';
    await api.put(`/events/${event.event_id}/tasks/${task.task_id}`, { status: next });
    load();
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    await api.delete(`/events/${event.event_id}/tasks/${taskId}`);
    load();
  };

  const applyTemplate = async (tplId, clearExisting) => {
    if (clearExisting && tasks.length > 0 && !window.confirm(`This will REPLACE all ${tasks.length} existing tasks. Continue?`)) return;
    try {
      const r = await api.post(`/events/${event.event_id}/tasks/from-template`, {
        template_id: tplId, clear_existing: clearExisting,
      });
      setShowTemplates(false);
      load();
      alert(`Added ${r.data.added} tasks from "${r.data.template}"`);
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (t) => t.status !== 'done' && t.resolved_due_date && t.resolved_due_date < today;
  const filtered = tasks.filter(t => {
    if (filter === 'open') return t.status !== 'done';
    if (filter === 'done') return t.status === 'done';
    if (filter === 'overdue') return isOverdue(t);
    return true;
  });
  const overdueCount = tasks.filter(isOverdue).length;

  // Group by resolved due date "bucket"
  const groupBucket = (t) => {
    if (t.status === 'done') return 'Done';
    if (!t.resolved_due_date) return 'No date set';
    const d = t.resolved_due_date;
    if (d < today) return 'Overdue';
    if (d === today) return 'Today';
    const tom = new Date(); tom.setDate(tom.getDate() + 1);
    if (d === tom.toISOString().slice(0, 10)) return 'Tomorrow';
    const weekEnd = new Date(); weekEnd.setDate(weekEnd.getDate() + 7);
    if (d <= weekEnd.toISOString().slice(0, 10)) return 'This week';
    return 'Later';
  };
  const order = ['Overdue', 'Today', 'Tomorrow', 'This week', 'Later', 'No date set', 'Done'];
  const grouped = order.map(g => ({ name: g, items: filtered.filter(t => groupBucket(t) === g) })).filter(g => g.items.length > 0);

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-3" data-testid="tasks-section">
      {/* Header */}
      <div className="glass-card p-4 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <h3 className="font-heading text-sm font-bold">Task Checklist</h3>
          <p className="text-xs text-neutral-400 mt-1">
            <span className="text-white font-semibold">{counts.open}</span> open · <span className="text-green-400 font-semibold">{counts.done}</span> done
            {overdueCount > 0 && <span className="text-red-400"> · {overdueCount} overdue</span>}
          </p>
        </div>
        <button onClick={() => setShowTemplates(s => !s)} data-testid="tasks-templates-btn"
          className="px-3 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5 flex items-center gap-1.5">
          <Sparkle size={14} /> Apply Template
        </button>
        <button onClick={() => setShowAdd(true)} data-testid="tasks-add-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold flex items-center gap-1.5 hover:opacity-90">
          <Plus size={14} weight="bold" /> Add Task
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-1 p-1 rounded-full w-fit" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {[
          { id: 'all', label: `All (${tasks.length})` },
          { id: 'open', label: `Open (${counts.open})` },
          { id: 'overdue', label: `Overdue (${overdueCount})`, color: '#EF4444' },
          { id: 'done', label: `Done (${counts.done})` },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`tasks-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all ${filter === f.id ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'text-neutral-400 hover:text-white'}`}
            style={f.color && filter === f.id ? { color: f.color, background: `${f.color}18` } : {}}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Templates picker */}
      {showTemplates && (
        <div className="glass-card p-4 border border-[#FF6B35]/30" data-testid="templates-picker">
          <h4 className="font-heading text-sm font-bold mb-2">Pre-built templates for Indian events</h4>
          <p className="text-xs text-neutral-400 mb-3">
            Due dates auto-calculated relative to your event date ({event.event_date || 'set the event date first'}).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {templates.map(t => (
              <div key={t.id} className="rounded-xl bg-white/[0.04] border border-white/10 p-3 hover:bg-white/[0.06] transition-colors">
                <div className="text-2xl mb-1">{t.icon}</div>
                <div className="text-xs font-semibold text-white">{t.label}</div>
                <div className="text-[10px] text-neutral-400 mb-2">{t.task_count} tasks</div>
                <div className="flex gap-1">
                  <button onClick={() => applyTemplate(t.id, false)} data-testid={`tpl-add-${t.id}`}
                    className="flex-1 px-2 py-1 rounded-full bg-[#FF6B35]/15 text-[#FF6B35] text-[10px] font-bold hover:bg-[#FF6B35]/25">Add</button>
                  <button onClick={() => applyTemplate(t.id, true)} data-testid={`tpl-replace-${t.id}`}
                    title="Replace all existing tasks"
                    className="flex-1 px-2 py-1 rounded-full border border-white/10 text-neutral-300 text-[10px] font-bold hover:bg-white/5">Replace</button>
                </div>
              </div>
            ))}
          </div>
          <div className="text-right mt-2">
            <button onClick={() => setShowTemplates(false)} className="text-xs text-neutral-400 hover:text-white">Close</button>
          </div>
        </div>
      )}

      {showAdd && (
        <TaskForm event={event} onCancel={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }} />
      )}

      {editing && (
        <TaskForm event={event} initial={editing} isEdit
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {/* Task groups */}
      {grouped.length === 0 ? (
        <div className="glass-card p-8 text-center" data-testid="tasks-empty">
          <CheckSquare size={32} className="mx-auto text-neutral-500 mb-2" />
          <p className="text-sm text-neutral-400">
            {tasks.length === 0 ? 'No tasks yet. Apply a template above or add tasks manually.' : 'No tasks match this filter.'}
          </p>
        </div>
      ) : (
        grouped.map(g => (
          <div key={g.name}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-2 pl-1 flex items-center gap-2"
              style={g.name === 'Overdue' ? { color: '#EF4444' } : g.name === 'Today' ? { color: '#F59E0B' } : {}}>
              {g.name} <span className="text-neutral-600">({g.items.length})</span>
            </div>
            <div className="space-y-1.5">
              {g.items.map(t => (
                <TaskRow key={t.task_id} task={t} event={event}
                  isOverdue={isOverdue(t)}
                  onToggle={() => toggleDone(t)}
                  onEdit={() => setEditing(t)}
                  onDelete={() => handleDelete(t.task_id)} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function TaskRow({ task, event, isOverdue, onToggle, onEdit, onDelete }) {
  const done = task.status === 'done';
  const priColor = PRIORITIES.find(p => p.id === task.priority)?.color || '#3B82F6';

  const fmtDate = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d + 'T00:00:00');
      return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch { return d; }
  };

  const reminderUrl = () => {
    if (!task.assigned_phone) return null;
    const due = task.resolved_due_date ? ` (due ${fmtDate(task.resolved_due_date)})` : '';
    const msg = `Hi ${task.assigned_name || 'there'}, friendly reminder for "${event.title}": ${task.title}${due}.${task.notes ? ' Notes: ' + task.notes : ''}`;
    const ph = task.assigned_phone.replace(/[^\d+]/g, '');
    return `https://wa.me/${ph}?text=${encodeURIComponent(msg)}`;
  };
  const url = reminderUrl();

  return (
    <div data-testid={`task-${task.task_id}`}
      className={`glass-card px-3 py-2.5 flex items-center gap-3 flex-wrap ${done ? 'opacity-60' : ''} ${isOverdue ? 'border-l-2 border-red-500' : ''}`}>
      <button onClick={onToggle} data-testid={`task-toggle-${task.task_id}`}
        className="shrink-0 text-neutral-400 hover:text-[#FF6B35]">
        {done ? <CheckSquare size={20} weight="fill" className="text-green-400" /> : <Square size={20} />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${done ? 'line-through text-neutral-500' : 'text-white'}`}>{task.title}</div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap text-[10px] text-neutral-400">
          <span className="px-1.5 py-0.5 rounded-full" style={{ background: `${priColor}18`, color: priColor }}>
            <Flag size={9} className="inline mr-0.5" />{task.priority}
          </span>
          {task.category && <span>· {task.category}</span>}
          {task.resolved_due_date && (
            <span className={isOverdue ? 'text-red-400 font-bold' : ''}>
              <Calendar size={9} className="inline mr-0.5" />{fmtDate(task.resolved_due_date)}
              {task.days_before_event != null && <> ({task.days_before_event === 0 ? 'event day' : `${task.days_before_event}d before`})</>}
            </span>
          )}
          {task.assigned_name && (
            <span>
              <User size={9} className="inline mr-0.5" />{task.assigned_name}
            </span>
          )}
          {task.notes && <span className="text-neutral-500 italic">{task.notes}</span>}
        </div>
      </div>
      {url && !done && (
        <a href={url} target="_blank" rel="noopener noreferrer"
          data-testid={`task-remind-${task.task_id}`}
          title="Send WhatsApp reminder to assignee"
          className="p-1.5 rounded-lg border border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366]/10">
          <WhatsappLogo size={12} />
        </a>
      )}
      <button onClick={onEdit} data-testid={`task-edit-${task.task_id}`}
        className="p-1.5 rounded-lg border border-white/10 hover:bg-white/5 text-neutral-400 hover:text-white">
        <PencilSimple size={12} />
      </button>
      <button onClick={onDelete} data-testid={`task-delete-${task.task_id}`}
        className="p-1.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10">
        <Trash size={12} />
      </button>
    </div>
  );
}

function TaskForm({ event, initial, isEdit, onCancel, onSaved }) {
  const { api } = useAuth();
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({
    title: initial?.title || '',
    notes: initial?.notes || '',
    category: initial?.category || 'General',
    priority: initial?.priority || 'normal',
    dueMode: initial?.due_date ? 'absolute' : 'relative',
    days_before_event: initial?.days_before_event ?? '',
    due_date: initial?.due_date || '',
    assigned_to_member_id: initial?.assigned_to_member_id || '',
    assigned_name: initial?.assigned_name || '',
    assigned_phone: initial?.assigned_phone || '',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/members').then(r => setMembers(r.data || [])).catch(() => {});
  }, [api]);

  const pickMember = (id) => {
    const m = members.find(mm => mm.member_id === id);
    setForm(f => ({
      ...f,
      assigned_to_member_id: id,
      assigned_name: m?.name || '',
      assigned_phone: m?.phone || '',
    }));
  };

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      notes: form.notes,
      category: form.category,
      priority: form.priority,
      days_before_event: form.dueMode === 'relative' && form.days_before_event !== '' ? parseInt(form.days_before_event, 10) : null,
      due_date: form.dueMode === 'absolute' && form.due_date ? form.due_date : null,
      assigned_to_member_id: form.assigned_to_member_id,
      assigned_name: form.assigned_name,
      assigned_phone: form.assigned_phone,
    };
    try {
      if (isEdit) {
        await api.put(`/events/${event.event_id}/tasks/${initial.task_id}`, payload);
      } else {
        await api.post(`/events/${event.event_id}/tasks`, payload);
      }
      onSaved();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
    setSaving(false);
  };

  return (
    <div className="glass-card p-4 border border-[#FF6B35]/30 space-y-3" data-testid="task-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="md:col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">Title *</span>
          <input value={form.title} onChange={(e) => upd('title', e.target.value)}
            data-testid="task-title" placeholder="e.g. Order birthday cake"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </label>
        <label>
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">Priority</span>
          <select value={form.priority} onChange={(e) => upd('priority', e.target.value)} data-testid="task-priority"
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white">
            {PRIORITIES.map(p => <option key={p.id} value={p.id} className="bg-neutral-900">{p.label}</option>)}
          </select>
        </label>

        <label>
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">Category</span>
          <input value={form.category} onChange={(e) => upd('category', e.target.value)}
            data-testid="task-category" placeholder="Planning / Venue / Catering..."
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </label>

        <label className="md:col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">Notes</span>
          <input value={form.notes} onChange={(e) => upd('notes', e.target.value)}
            data-testid="task-notes" placeholder="Optional details, vendor contact, etc."
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
        </label>

        <div>
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">Due Date</span>
          <div className="flex gap-1 p-1 rounded-lg bg-white/5 mb-2">
            <button type="button" onClick={() => upd('dueMode', 'relative')}
              className={`flex-1 px-2 py-1 rounded text-[10px] font-bold ${form.dueMode === 'relative' ? 'bg-[#FF6B35] text-white' : 'text-neutral-400'}`}>
              Relative
            </button>
            <button type="button" onClick={() => upd('dueMode', 'absolute')}
              className={`flex-1 px-2 py-1 rounded text-[10px] font-bold ${form.dueMode === 'absolute' ? 'bg-[#FF6B35] text-white' : 'text-neutral-400'}`}>
              Specific date
            </button>
          </div>
          {form.dueMode === 'relative' ? (
            <input type="number" value={form.days_before_event} onChange={(e) => upd('days_before_event', e.target.value)}
              data-testid="task-days-before" placeholder="0 = event day, 7 = a week before"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
          ) : (
            <input type="date" value={form.due_date} onChange={(e) => upd('due_date', e.target.value)}
              data-testid="task-due-date"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
          )}
        </div>

        <div>
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">Assign to Member</span>
          <select value={form.assigned_to_member_id} onChange={(e) => pickMember(e.target.value)}
            data-testid="task-member" className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white">
            <option value="" className="bg-neutral-900">— Nobody / type below —</option>
            {members.map(m => (
              <option key={m.member_id} value={m.member_id} className="bg-neutral-900">
                {m.name}{m.phone ? ` (${m.phone})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2 grid grid-cols-2 gap-3">
          <label>
            <span className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">Or assignee name</span>
            <input value={form.assigned_name} onChange={(e) => upd('assigned_name', e.target.value)}
              data-testid="task-assigned-name" placeholder="e.g. Mom, Dad, Friend"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
          </label>
          <label>
            <span className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">Phone (for reminders)</span>
            <input value={form.assigned_phone} onChange={(e) => upd('assigned_phone', e.target.value)}
              data-testid="task-assigned-phone" placeholder="+91 9999..."
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white" />
          </label>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-4 py-2 rounded-full border border-white/10 text-xs font-medium hover:bg-white/5">Cancel</button>
        <button onClick={save} disabled={saving || !form.title.trim()} data-testid="task-save-btn"
          className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50">
          <FloppyDisk size={14} /> {saving ? 'Saving...' : 'Save Task'}
        </button>
      </div>
    </div>
  );
}
